import React, { useState, useEffect, useRef } from "react";
import {
  Slide,
  Fade,
  Box,
  Typography,
  Button,
  IconButton,
  ThemeProvider,
  createTheme,
  Collapse,
  Backdrop,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SigningAuthorityModal from "./SigningAuthorityModal";
import MOUSignatureModal from "./MOUSignatureModal";
import api from "../../../services/api";

// Supabase-inspired theme with blue (hsl(210, 60%, 53%))
const supabaseTheme = createTheme({
  palette: {
    primary: {
      main: "hsl(210, 60%, 53%)",
      light: "hsl(210, 60%, 63%)",
      dark: "hsl(210, 60%, 43%)",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "hsl(211, 54%, 45%)",
      light: "hsl(211, 54%, 55%)",
      dark: "hsl(211, 54%, 35%)",
      contrastText: "#ffffff",
    },
    background: {
      default: "hsl(0, 0%, 100%)",
      paper: "hsl(0, 0%, 98%)",
    },
    text: {
      primary: "hsl(0, 0%, 13%)",
      secondary: "hsl(0, 0%, 40%)",
    },
  },
  typography: {
    fontFamily:
      "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    button: {
      fontWeight: 600,
      textTransform: "none",
      letterSpacing: "-0.01em",
    },
    body1: {
      fontWeight: 500,
      letterSpacing: "-0.01em",
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    "none",
    "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    ...Array(18).fill("none"),
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: "6px 20px",
          fontSize: "14px",
          fontWeight: 600,
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
      },
    },
  },
});

const SigningAuthorityNotification = ({ onYes, onNo, onClose }) => {
  const [mounted, setMounted] = useState(false);
  const [show, setShow] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showMOUModal, setShowMOUModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [permanentlyHidden, setPermanentlyHidden] = useState(false);
  const [hasPendingMOU, setHasPendingMOU] = useState(false);
  const [accessType, setAccessType] = useState(null);
  const [mouId, setMouId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const containerRef = useRef(null);

  // Fetch pending MOU status
  useEffect(() => {
    const fetchPendingMOU = async () => {
      try {
        const accessToken = localStorage.getItem("accessToken");

        if (!accessToken) {
          console.error("No access token found");
          return;
        }

        const response = await api.get(`/accounts/my-pending-mou`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        const { has_pending_mou, mou_id, access_type } = response.data;

        setHasPendingMOU(has_pending_mou);
        setMouId(mou_id);
        setAccessType(access_type);

        // Only show notification if there's a pending MOU
        if (has_pending_mou) {
          setShow(true);
        }
      } catch (error) {
        console.error("Error fetching pending MOU:", error);
      }
    };

    fetchPendingMOU();
  }, []);

  // Smooth initial mount animation
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        setMounted(true);
      }, 100);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [show]);

  const handleYes = () => {
    setShow(false);
    setShowMOUModal(true);
  };

  const handleMOUSubmit = (signatureFile) => {
    console.log("MOU signed with file:", signatureFile);
    setShowMOUModal(false);
    setPermanentlyHidden(true);
    setTimeout(() => onYes && onYes(signatureFile), 300);
  };

  const handleMOUClose = () => {
    setShowMOUModal(false);
    setShow(true);
  };

  const handleNo = async () => {
    setShow(false);

    // Only show form modal if access_type is "original_spoc"
    if (accessType === "original_spoc") {
      setShowFormModal(true);
    } else if (accessType === "delegated_authority") {
      // For delegated_authority, call the decline API
      try {
        const accessToken = localStorage.getItem("accessToken");

        const formData = new FormData();
        formData.append("mou_id", mouId);

        const response = await api.post(
          "/accounts/delegated-decline",
          formData,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        alert(
          response?.data?.detail ||
            "MOU signing authority declined successfully."
        );

        // // Show success message
        // setSnackbar({
        //   open: true,
        //   message: "MOU signing authority declined successfully",
        //   severity: "success",
        // });

        setPermanentlyHidden(true);
        setTimeout(() => onNo && onNo(null), 300);
      } catch (error) {
        console.error("Error declining MOU:", error);

        // Show error message
        setSnackbar({
          open: true,
          message:
            error.response?.data?.message ||
            "Failed to decline MOU. Please try again.",
          severity: "error",
        });

        // Show the notification again if there was an error
        setShow(true);
      }
    }
  };

  const handleFormSubmit = (formData) => {
    console.log("Form submitted:", formData);
    setShowFormModal(false);
    setPermanentlyHidden(true);
    setTimeout(() => onNo && onNo(formData), 300);
  };

  const handleFormClose = () => {
    setShowFormModal(false);
    setShow(true);
  };

  const handleMinimize = (e) => {
    e.stopPropagation();
    setIsMinimized(true);
  };

  const handleExpand = (e) => {
    e.stopPropagation();
    setIsMinimized(false);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // Calculate if backdrop should be shown
  const showBackdrop = mounted && show && !isMinimized;

  // Don't render if no pending MOU or permanently hidden
  if (!hasPendingMOU || permanentlyHidden) {
    return (
      <>
        {/* Show snackbar even after component is hidden */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  }

  return (
    <>
      <ThemeProvider theme={supabaseTheme}>
        {/* Backdrop with blur effect - only show when not minimized */}
        <Backdrop
          open={showBackdrop}
          sx={{
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0, 0, 0, 0.15)",
            zIndex: 9998,
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            willChange: "backdrop-filter, opacity",
          }}
        />

        {/* Container for centering */}
        <Box
          ref={containerRef}
          sx={{
            position: "fixed",
            top: 20,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            width: "auto",
            maxWidth: "90%",
            pointerEvents: mounted && show ? "auto" : "none",
          }}
        >
          <Slide
            direction="down"
            in={mounted && show}
            timeout={{ enter: 500, exit: 300 }}
          >
            <Box>
              <Fade in={mounted && show} timeout={{ enter: 600, exit: 200 }}>
                <Box
                  onClick={isMinimized ? handleExpand : undefined}
                  sx={{
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    borderRadius: 2,
                    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
                    fontFamily: (theme) => theme.typography.fontFamily,
                    cursor: isMinimized ? "pointer" : "default",
                    transition: "box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    overflow: "hidden",
                    willChange: "box-shadow",
                    "&:hover": {
                      boxShadow: isMinimized
                        ? "0 12px 45px rgba(0, 0, 0, 0.25)"
                        : "0 10px 40px rgba(0, 0, 0, 0.2)",
                    },
                  }}
                >
                  {/* Minimized Peek Bar */}
                  {isMinimized && (
                    <Box
                      sx={{
                        height: "4px",
                        width: "200px",
                        background:
                          "linear-gradient(90deg, hsl(210, 60%, 53%) 0%, hsl(210, 60%, 63%) 100%)",
                        position: "relative",
                        borderRadius: 2,
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: "100%",
                          background:
                            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)",
                          animation: "shimmer 2s infinite",
                        },
                        "@keyframes shimmer": {
                          "0%": {
                            transform: "translateX(-100%)",
                          },
                          "100%": {
                            transform: "translateX(100%)",
                          },
                        },
                      }}
                    />
                  )}

                  {/* Full Content */}
                  <Collapse in={!isMinimized} timeout={300}>
                    <Box
                      sx={{
                        padding: "14px 20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        minWidth: "fit-content",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: "14px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Are you the Signing Authority for Signing the MOU?
                      </Typography>

                      <Box sx={{ display: "flex", gap: 1.5 }}>
                        <Button
                          onClick={handleYes}
                          variant="contained"
                          size="small"
                          sx={{
                            backgroundColor: "primary.contrastText",
                            color: "primary.main",
                            transition: "background-color 0.2s ease",
                            willChange: "background-color",
                            "&:hover": {
                              backgroundColor: "background.paper",
                            },
                          }}
                        >
                          Yes
                        </Button>

                        <Button
                          onClick={handleNo}
                          variant="outlined"
                          size="small"
                          sx={{
                            borderColor: "primary.contrastText",
                            color: "primary.contrastText",
                            transition: "background-color 0.2s ease",
                            willChange: "background-color",
                            "&:hover": {
                              borderColor: "primary.contrastText",
                              backgroundColor: "rgba(255, 255, 255, 0.1)",
                            },
                          }}
                        >
                          No
                        </Button>
                      </Box>

                      <IconButton
                        onClick={handleMinimize}
                        size="small"
                        sx={{
                          color: "primary.contrastText",
                          padding: "4px",
                          transition: "background-color 0.2s ease",
                          willChange: "background-color",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                          },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Collapse>
                </Box>
              </Fade>
            </Box>
          </Slide>
        </Box>

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </ThemeProvider>

      {/* Form Modal (for No) - only show for original_spoc */}
      {accessType === "original_spoc" && (
        <SigningAuthorityModal
          open={showFormModal}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          mouId={mouId}
        />
      )}

      {/* MOU Signature Modal (for Yes) */}
      <MOUSignatureModal
        open={showMOUModal}
        onClose={handleMOUClose}
        onSubmit={handleMOUSubmit}
      />
    </>
  );
};

export default SigningAuthorityNotification;
